import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {InputField} from '@Components/input-field-v2';
import type {InputFieldControlItemData, InputFieldControlItemEvent} from '@Components/input-field-v2/input-field.types';
import {InputControlItemType} from '@Components/input-field-v2/input-field.types';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import type {GridMultipageItemStorage} from '@Components/base-grid/components/grid-multipage-item';
import {getNoOfGridItems, reloadGrid, updateGridItem} from '@Components/base-grid';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './poster-template-panel.module.scss';
import {TemplateFilters} from '@Components/poster-editor/components/poster-add-item-bar/components/template-filters/template-filters';
import {NoTemplateResults} from '@Components/poster-editor/components/poster-add-item-bar/components/no-template-results/no-template-results';
import {LazyMasonryGrid} from '@Components/lazy-masonry-grid';
import type {LazyLoadingGetterParams, LazyLoadingResponse} from '@Libraries/pmw-stock-media-library';
import {isUserLoggedIn} from '@Libraries/user.library';
import {getUserSafeSearchPreferenceFromCookie, updateSafeSearchUserPreferenceInDB, updateUserSafeSearchPreferenceCookie} from '@Utils/user.util';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import useWindowSize from '@Hooks/useWindowSize';
import type {MultipageTemplateData, SearchObject, TemplateFilterOptionData, TemplatesFiltersData} from '@Libraries/editor-templates-library';
import {
  getDataForTemplates,
  getFiltersForTemplatesInEditor,
  getRecommendedDataForTemplates,
  isTemplateGridHeightBelowThreshold,
  RECOMMENDED_TEMPLATES_GRID_ID,
  TEMPLATE_GRID_BATCH_SIZE,
  TEMPLATE_GRID_ITEM_DEFAULT_WEB_WIDTH,
  TemplateOrientationTypes,
  TEMPLATES_GRID_ID,
  TEMPLATES_GRID_ITEM_HEIGHT_THRESHOLD,
} from '@Libraries/editor-templates-library';
import {useHandleViewsAndSeenTemplateStats} from '@Hooks/poster-editor/useHandleViewsAndSeenTemplateStats';
import {isCustomPosterType, toggleTemplatesPopupOnBoardingToSeen} from '@Components/poster-editor/library/poster-editor-library';
import isEqual from 'lodash/isEqual';
import {setFiltersData} from '@Components/poster-template-panel/poster-template-panel.reducer';
import {GA4EventName, GA4EventParamName, GA4TemplateAddTypes, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {ChristmasFilter, HalloweenFilter} from '@Components/poster-editor/components/poster-add-item-bar/components/template-filters/template-filters.types';
import {isChristmasSeason, isHalloweenSeason} from '@Utils/seasonal-promotion.util';
import {isWebpSupported} from '@Utils/browser.util';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {noop} from '@Utils/general.util';

interface PosterTemplatePanelProps {
  isOpenedInMobileModal?: boolean;

  onAddTemplate(hashId: string): Promise<void>;
  onMultipageTemplateClick?: (template: MultipageTemplateData) => void;

  showBackButton?: boolean;
  onBack?(): void;
  posterHeight: number;
  posterWidth: number;
  sendHeightWidthParamsForPosterType?: boolean;
  posterTypeName?: string;
  idGalleryTemplate?: string;
  isContentVisibleToUser?: boolean;
}

export function PosterTemplatePanel({
  isOpenedInMobileModal = false,
  posterTypeName = '',
  idGalleryTemplate = '',
  sendHeightWidthParamsForPosterType = false,
  isContentVisibleToUser = true,
  showBackButton = false,
  onBack = noop,
  ...props
}: PosterTemplatePanelProps): ReactElement<any> {
  const idGalleryTemplateRef = useRef(idGalleryTemplate);
  const idGalleryTemplateForResults = useRef<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const searchRef = useRef<string>('');
  const scrollingContainerRef = useRef<HTMLDivElement>(null);
  const savedScrollTop = useRef<number>(0);
  const designsYouMayLikeContainerRef = useRef<HTMLDivElement | null>(null);
  const {topShadow} = useTopBottomScrollShadow(scrollingContainerRef);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string>>({});
  const [noSearchResults, setNoSearchResults] = useState<boolean>(false);
  const [gridItemHeight, setGridItemHeight] = useState<number | null>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const batchNumber = useRef<number>(0);
  const batchOffset = useRef<number>(0);
  const [loading, setLoading] = useState(false);
  const safeSearch = useRef(getUserSafeSearchPreferenceFromCookie());
  const multipage = useRef(false);
  const filtersData = useRef<TemplatesFiltersData>(undefined);
  const [isSafeSearchUpdating, setIsSafeSearchUpdating] = useState<boolean>(false);
  const [isMultipageUpdating, setIsMultipageUpdating] = useState<boolean>(false);
  const isApplying = useRef(false);
  const {windowWidth} = useWindowSize();
  const GRID_ITEM_DEFAULT_WIDTH = isOpenedInMobileModal ? windowWidth / 2 - 20 : TEMPLATE_GRID_ITEM_DEFAULT_WEB_WIDTH;
  const dispatch = useAppDispatch();
  const filtersDataFromRedux = useAppSelector((state) => {
    return state.templatesPopup.filters;
  });
  const numberOfGridItems = useAppSelector((state) => {
    return getNoOfGridItems(state, TEMPLATES_GRID_ID);
  });
  const previousSearchValue = useRef<string>('');
  useHandleViewsAndSeenTemplateStats(
    scrollingContainerRef,
    gridItemHeight,
    isTemplateGridHeightBelowThreshold(gridItemHeight) ? GRID_ITEM_DEFAULT_WIDTH * 2 : GRID_ITEM_DEFAULT_WIDTH,
    designsYouMayLikeContainerRef
  );

  const reloadDesignsYouMayLikeGrid = (): void => {
    dispatch(reloadGrid({gridId: RECOMMENDED_TEMPLATES_GRID_ID, gridSelectionGroupId: TEMPLATES_GRID_ID}));
  };

  useEffect((): void => {
    if ((!isContentVisibleToUser && !idGalleryTemplate) || idGalleryTemplate !== idGalleryTemplateForResults.current) {
      reloadDesignsYouMayLikeGrid();
      idGalleryTemplateRef.current = idGalleryTemplate;
    }

    if (isContentVisibleToUser && scrollingContainerRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (scrollingContainerRef.current) {
            scrollingContainerRef.current.scrollTop = savedScrollTop.current;
          }
        });
      });
    }
  }, [isContentVisibleToUser]);

  const getSeasonalFilters = (): TemplateFilterOptionData[] => {
    const seasonFilters = [];
    if (isHalloweenSeason()) {
      seasonFilters.push({
        id: Number(HalloweenFilter.id),
        displayName: window.i18next.t('pmwjs_halloween'),
        filterOption: HalloweenFilter.type,
      });
    }
    if (isChristmasSeason()) {
      seasonFilters.push({
        id: Number(ChristmasFilter.id),
        displayName: window.i18next.t('pmwjs_christmas'),
        filterOption: ChristmasFilter.type,
      });
    }
    return seasonFilters;
  };

  const getFilters = async (): Promise<void> => {
    if (Object.keys(filtersDataFromRedux).length > 0) {
      filtersData.current = filtersDataFromRedux;
      return;
    }
    try {
      const filters = await getFiltersForTemplatesInEditor();
      filters.design_style.optionsData = [...getSeasonalFilters(), ...filters.design_style.optionsData];
      filtersData.current = filters;
      dispatch(setFiltersData(filters));
    } catch (err) {
      console.error(err);
    }
  };

  const setHeightFromPosterType = (): void => {
    setGridItemHeight((props.posterHeight * GRID_ITEM_DEFAULT_WIDTH) / props.posterWidth);
  };

  const reloadGridState = (): void => {
    batchNumber.current = 0;
    batchOffset.current = 0;
    dispatch(
      reloadGrid({
        gridId: TEMPLATES_GRID_ID,
        gridSelectionGroupId: TEMPLATES_GRID_ID,
      })
    );
  };

  useEffect((): void => {
    setHeightFromPosterType();
  }, [props.posterHeight, props.posterWidth]);

  useEffect(() => {
    void getFilters();
    toggleTemplatesPopupOnBoardingToSeen();
  }, []);

  const getDataFromFilters = (): Record<string, string> => {
    const keys: Record<string, string> = {};
    if (!filtersData.current) {
      return keys;
    }

    if (!Object.keys(selectedFilters).length) {
      return keys;
    }

    for (const [key, value] of Object.entries(selectedFilters)) {
      if (value) {
        let filterValue: TemplateFilterOptionData | undefined = undefined;

        if (key === 'contentType') {
          filterValue = filtersData.current[key].optionsData.find((obj) => String(obj.id) === value);
        } else {
          filterValue = filtersData.current[key].optionsData.find((obj) => obj.id === Number(value));
        }

        if (filterValue && filterValue.id !== Number(HalloweenFilter.id)) {
          keys[key] = filterValue.filterOption;
        }
      }
    }

    return keys;
  };

  const setSelectedSeasonalFilter = (): string => {
    if (!filtersData.current) {
      return '';
    }
    if (!Object.keys(selectedFilters).length) {
      return '';
    }
    for (const [key, value] of Object.entries(selectedFilters)) {
      if (value) {
        let filter: TemplateFilterOptionData | undefined = undefined;
        if (key === 'contentType') {
          filter = filtersData.current[key].optionsData.find((obj) => String(obj.id) === value);
        } else {
          filter = filtersData.current[key].optionsData.find((obj) => obj.id === Number(value));
        }
        if (filter?.id === Number(HalloweenFilter.id)) {
          return HalloweenFilter.type;
        }
        if (filter?.id === Number(ChristmasFilter.id)) {
          return ChristmasFilter.type;
        }
        return '';
      }
    }
    return '';
  };

  const getOrientationFilter = (): Record<string, string> => {
    const orientationFilter: Record<string, string> = {};
    if (!window.posterEditor?.whiteboard) {
      return orientationFilter;
    }

    const ratio = props.posterWidth / props.posterHeight;
    if (ratio < 0.9) {
      orientationFilter.orientation = TemplateOrientationTypes.Portrait;
    } else if (ratio > 1.1) {
      orientationFilter.orientation = TemplateOrientationTypes.Landscape;
    } else {
      orientationFilter.orientation = TemplateOrientationTypes.Square;
    }
    return orientationFilter;
  };

  const getPosterTypeParam = (): Record<string, string | undefined | number> => {
    if (sendHeightWidthParamsForPosterType) {
      return {
        width: window.posterEditor?.whiteboard?.userWidth,
        height: window.posterEditor?.whiteboard?.userHeight,
      };
    }

    if (posterTypeName) {
      return {
        posterType: posterTypeName,
      };
    }

    return {};
  };

  const getSafeSearchFilter = (): Record<string, string> => {
    if (safeSearch.current) {
      return {
        safe_content: 'safe',
      };
    }

    return {};
  };

  const getMultipageFilter = (): Record<string, string> => {
    if (multipage.current) {
      return {
        multipage: 'true',
      };
    }

    return {};
  };

  const getRecommendedData = async (): Promise<SearchObject> => {
    idGalleryTemplateForResults.current = idGalleryTemplate;
    return await getRecommendedDataForTemplates({
      idGalleryTemplate: idGalleryTemplate,
    });
  };

  const getData = async (getterParams: LazyLoadingGetterParams): Promise<SearchObject> => {
    previousSearchValue.current = search;

    return await getDataForTemplates({
      searchTerm: `${search} ${setSelectedSeasonalFilter()}`,
      limit: getterParams.limit,
      offset: numberOfGridItems,
      batchOffset: batchOffset.current,
      batchNumber: batchNumber.current,
      ...getSafeSearchFilter(),
      ...getMultipageFilter(),
      ...getDataFromFilters(),
      ...getOrientationFilter(),
      ...getPosterTypeParam(),
    });
  };

  const getDataForGridItems = (data: SearchObject, isForDesignsYouMayLike = false): LazyLoadingResponse => {
    const gridItems = [];
    for (const eachPosterData of data.posters) {
      const data = {
        variantOfTemplateId: eachPosterData.variantOfTemplateId,
        idcreator: eachPosterData.idcreator,
        isViewed: false,
        isSeen: false,
        isDataIncremented: false,
      };
      const thumbSrc = isWebpSupported() ? eachPosterData.previewURLs[0] : eachPosterData.fallbackPreviewURLs[0];
      const previewURLs = isWebpSupported() ? eachPosterData.previewURLs : eachPosterData.fallbackPreviewURLs;
      const gridItemWidth = isTemplateGridHeightBelowThreshold(gridItemHeight) ? GRID_ITEM_DEFAULT_WIDTH * 2 : GRID_ITEM_DEFAULT_WIDTH;
      const gridItemHeightValue = isTemplateGridHeightBelowThreshold(gridItemHeight) ? TEMPLATES_GRID_ITEM_HEIGHT_THRESHOLD : gridItemHeight;

      if (eachPosterData.isMultiPage) {
        gridItems.push({
          type: GRID_ITEM_TYPE.MULTIPAGE,
          id: eachPosterData.hashedID,
          previewURLs,
          fallbackPreviewURLs: eachPosterData.fallbackPreviewURLs,
          videoPreviewURLs: eachPosterData.videoPreviewURLs || [],
          pagesIsVideo: eachPosterData.pagesIsVideo || [],
          pageCount: eachPosterData.pageCount,
          pageHashedIDs: eachPosterData.pageHashedIDs ?? [],
          templateName: eachPosterData.templateName ?? '',
          width: gridItemWidth,
          height: gridItemHeightValue,
          data,
        });
      } else if (eachPosterData.isVideo) {
        gridItems.push({
          type: GRID_ITEM_TYPE.VIDEO,
          id: eachPosterData.hashedID,
          thumbSrc,
          videoSrc: eachPosterData.videoPreviewURLs[0],
          width: gridItemWidth,
          height: gridItemHeightValue,
          showDurationTag: false,
          showVideoTag: true,
          data,
        });
      } else {
        gridItems.push({
          type: GRID_ITEM_TYPE.IMAGE,
          id: eachPosterData.hashedID,
          thumbSrc,
          width: gridItemWidth,
          height: gridItemHeightValue,
          data,
        });
      }
    }

    if (noSearchResults && gridItems.length > 0 && !isForDesignsYouMayLike) {
      setNoSearchResults(false);
    }
    if (gridItems.length === 0 && !isForDesignsYouMayLike) {
      setNoSearchResults(true);
    }

    return {
      response: gridItems,
      offsetsData: {
        DEFAULT_SOURCE: gridItems.length,
        batchNumber: data.batchNumber,
        batchOffset: data.batchOffset,
      },
    };
  };

  const onClickControls = (icon: string, e: InputFieldControlItemEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    if (icon === 'cancel') {
      setSearch('');
      searchRef.current = '';
      if (previousSearchValue.current !== '') {
        reloadGridState();
        reloadDesignsYouMayLikeGrid();
      }
    } else {
      searchRef.current = search;
      onClickSearch();
    }
  };

  const onClickSearch = (): void => {
    if (previousSearchValue.current !== search) {
      reloadGridState();
      reloadDesignsYouMayLikeGrid();
    }
  };

  const getControlsForSearch = (): InputFieldControlItemData[] => {
    const controls: InputFieldControlItemData[] = [];

    if (search !== '') {
      controls.push({
        id: 'cancel',
        icon: 'icon-close',
        type: InputControlItemType.ICONS_ONLY,
      });
    }

    controls.push({
      id: 'search',
      icon: 'icon-search',
      type: search !== '' ? InputControlItemType.ICON_BTNS : InputControlItemType.ICONS_ONLY,
    });

    return controls;
  };

  const getSearchBar = (): ReactElement<any> => {
    return (
      <InputField
        className={`${styles.searchBar} ${isOpenedInMobileModal ? 'spacing-m-t-3' : 'spacing-m-t-4'} spacing-m-b-2 spacing-m-l-4 spacing-m-r-4`}
        isControlled
        input={search}
        placeholder={window.i18next.t('pmwjs_search_templates_placeholder')}
        id="search-input"
        onInputChange={(value: string) => {
          setSearch(value);
        }}
        onInputSubmit={(): void => {
          searchRef.current = search;
          onClickSearch();
        }}
        controls={{
          showDefaultControls: false,
          showSingleDefaultControlOnly: false,
          customControls: getControlsForSearch(),
          onClick: onClickControls,
        }}
      />
    );
  };

  const toggleLoadingState = (id: string, isLoading: boolean, isForRecommendedGrid = false): void => {
    isApplying.current = isLoading;
    dispatch(
      updateGridItem({
        gridId: isForRecommendedGrid ? RECOMMENDED_TEMPLATES_GRID_ID : TEMPLATES_GRID_ID,
        itemId: id,
        changes: {
          isLoading,
        },
      })
    );
  };

  const getGridItemById = (id: string, isForRecommendedGrid: boolean): GridItemStorage | null => {
    const gridId = isForRecommendedGrid ? RECOMMENDED_TEMPLATES_GRID_ID : TEMPLATES_GRID_ID;
    const gridState = window.PMW.redux.store.getState().grids.gridsHashmap[gridId];
    return gridState?.items?.find((item) => item.id === id) || null;
  };

  const handleGridItemClick = async (id: string, isForRecommendedGrid = false): Promise<void> => {
    if (isApplying.current) {
      return;
    }

    const gridItem = getGridItemById(id, isForRecommendedGrid);

    if (gridItem?.type === GRID_ITEM_TYPE.MULTIPAGE) {
      const multipageItem = gridItem as GridMultipageItemStorage;
      const templateData: MultipageTemplateData = {
        hashedID: id,
        previewURLs: multipageItem.previewURLs,
        fallbackPreviewURLs: multipageItem.fallbackPreviewURLs,
        videoPreviewURLs: multipageItem.videoPreviewURLs || [],
        pagesIsVideo: multipageItem.pagesIsVideo || [],
        pageCount: multipageItem.pageCount,
        pageHashedIDs: multipageItem.pageHashedIDs || [],
        templateName: multipageItem.templateName || '',
      };

      if (props.onMultipageTemplateClick) {
        if (scrollingContainerRef.current) {
          savedScrollTop.current = scrollingContainerRef.current.scrollTop;
        }
        props.onMultipageTemplateClick(templateData);
      }
      return;
    }

    toggleLoadingState(id, true, isForRecommendedGrid);
    await props.onAddTemplate(id);
    toggleLoadingState(id, false, isForRecommendedGrid);
  };

  const setHeightIfNotSet = (): void => {
    setHeightFromPosterType();
  };

  const getRecommendedItems = async (): Promise<LazyLoadingResponse> => {
    try {
      const data = await getRecommendedData();
      return getDataForGridItems(data, true);
    } catch (err) {
      console.error(err);
      return {
        response: [],
        offsetsData: {},
      };
    }
  };

  const getItems = async (getterParams: LazyLoadingGetterParams): Promise<LazyLoadingResponse> => {
    setLoading(true);

    setHeightIfNotSet();
    try {
      const data = await getData(getterParams);
      batchNumber.current = data.batchNumber;
      batchOffset.current = data.batchOffset;

      setIsError(false);
      return getDataForGridItems(data);
    } catch (err) {
      setIsError(true);
      console.error(err);
      return {
        response: [],
        offsetsData: {},
      };
    } finally {
      setLoading(false);
    }
  };

  const getRecommendedDesignsGrid = (): ReactElement<any> | null => {
    if (idGalleryTemplateRef.current === '' || areFiltersApplied() || searchRef.current !== '') {
      return null;
    }
    return (
      <LazyMasonryGrid
        gridRef={(ref) => {
          designsYouMayLikeContainerRef.current = ref;
        }}
        className={`spacing-p-r-0 spacing-p-l-0 spacing-p-t-0 spacing-p-b-3 _overflow-hidden spacing-m-b-3 ${styles.borderBottom}`}
        rowClassName={styles.gap}
        gridContainerClassName={styles.gap}
        gridId={RECOMMENDED_TEMPLATES_GRID_ID}
        clearGridOnUnmount
        gridSelectionGroupId={TEMPLATES_GRID_ID}
        loadItemBatchSize={TEMPLATE_GRID_BATCH_SIZE}
        maxSelectionNumber={1}
        getItems={(): Promise<LazyLoadingResponse> => {
          return getRecommendedItems();
        }}
        customDimensions={{
          width: isTemplateGridHeightBelowThreshold(gridItemHeight) ? GRID_ITEM_DEFAULT_WIDTH * 2 : GRID_ITEM_DEFAULT_WIDTH,
          height: !gridItemHeight || isTemplateGridHeightBelowThreshold(gridItemHeight) ? TEMPLATES_GRID_ITEM_HEIGHT_THRESHOLD : gridItemHeight,
        }}
        customOnGridItemClicked={(gridItem: GridItemStorage): void => {
          trackPosterBuilderGA4Events(GA4EventName.EDITOR_TEMPLATE_ADD, {
            [GA4EventParamName.TYPE]: GA4TemplateAddTypes.RECOMMENDED,
          });
          void handleGridItemClick(gridItem.id, true);
        }}
        scrollingContainer={scrollingContainerRef.current}
        doCheckForNextBatchImmediately={false}
      />
    );
  };

  const getDesigns = (): ReactElement<any> => {
    return (
      <LazyMasonryGrid
        className="spacing-p-r-0 _overflow-hidden"
        rowClassName={styles.gap}
        gridContainerClassName={styles.gap}
        gridId={TEMPLATES_GRID_ID}
        gridSelectionGroupId={TEMPLATES_GRID_ID}
        getItems={(getterParams): Promise<LazyLoadingResponse> => {
          return getItems(getterParams);
        }}
        clearGridOnUnmount
        loadItemBatchSize={TEMPLATE_GRID_BATCH_SIZE}
        maxSelectionNumber={1}
        customDimensions={{
          width: isTemplateGridHeightBelowThreshold(gridItemHeight) ? GRID_ITEM_DEFAULT_WIDTH * 2 : GRID_ITEM_DEFAULT_WIDTH,
          height: !gridItemHeight || isTemplateGridHeightBelowThreshold(gridItemHeight) ? TEMPLATES_GRID_ITEM_HEIGHT_THRESHOLD : gridItemHeight,
        }}
        customOnGridItemClicked={(gridItem: GridItemStorage): void => {
          trackPosterBuilderGA4Events(GA4EventName.EDITOR_TEMPLATE_ADD, {
            [GA4EventParamName.TYPE]: GA4TemplateAddTypes.DEFAULT,
          });
          void handleGridItemClick(gridItem.id);
        }}
        scrollingContainer={scrollingContainerRef.current}
        doCheckForNextBatchImmediately={false}
      />
    );
  };

  const areFiltersApplied = (): boolean => {
    if (!Object.keys(selectedFilters).length) {
      return false;
    }

    for (const [, value] of Object.entries(selectedFilters)) {
      if (value) {
        return true;
      }
    }

    return false;
  };

  const getNoResultsUI = (): ReactElement<any> | null => {
    if (!loading && (noSearchResults || isError)) {
      return (
        <NoTemplateResults
          isGenericError={isError}
          isOpenedInMobileModal={isOpenedInMobileModal}
          areFiltersApplied={areFiltersApplied()}
          isCustomPosterType={isCustomPosterType()}
          search={search}
        />
      );
    }
    return null;
  };

  const onFilterUpdateCallback = (filters: Record<string, string>): void => {
    if (isEqual(filters, selectedFilters)) {
      return;
    }
    setSelectedFilters(filters);
    reloadGridState();
    reloadDesignsYouMayLikeGrid();
  };

  const updateUserSafeSearchPreference = async (preference: boolean): Promise<void> => {
    setIsSafeSearchUpdating(true);
    if (isUserLoggedIn()) {
      try {
        await updateSafeSearchUserPreferenceInDB(Number(preference));
      } catch (err) {
        console.log(err);
      }
    }
    updateUserSafeSearchPreferenceCookie(Number(preference));

    safeSearch.current = preference;
    setIsSafeSearchUpdating(false);
    reloadGridState();
  };

  const onToggleSafeSearch = (val: boolean): void => {
    void updateUserSafeSearchPreference(val);
  };

  const updateMultipagePreference = async (preference: boolean): Promise<void> => {
    setIsMultipageUpdating(true);
    multipage.current = preference;
    setIsMultipageUpdating(false);
    reloadGridState();
  };

  const onToggleMultipage = (val: boolean): void => {
    void updateMultipagePreference(val);
  };

  const getHeadingWithBackButton = (): ReactElement | null => {
    if (!showBackButton) {
      return null;
    }
    return (
      <div className="spacing-p-l-4 spacing-p-t-4 flex-h-row flex-items-center">
        <Icon
          onClick={(): void => {
            onBack();
          }}
          className="spacing-m-r-2"
          icon={'icon-arrow-left'}
          size={IconSize.SIZE_ICON_16}
          shape={IconShape.SQUARE}
          type={IconType.GHOST}
        />
        <Text className="capitalize-sentence" bold val={window.i18next.t('pmwjs_templates')} size={TextSize.SMALL} />
      </div>
    );
  };

  return (
    <div className="flex-v-row _full-height _overflow-hidden">
      {getHeadingWithBackButton()}
      {getSearchBar()}
      <TemplateFilters
        onToggleSafeSearch={onToggleSafeSearch}
        safeSearch={safeSearch.current}
        filtersData={filtersData.current}
        selectedFilters={selectedFilters}
        onFilterUpdate={onFilterUpdateCallback}
        isSafeSearchBeingUpdated={isSafeSearchUpdating}
        multipage={multipage.current}
        onToggleMultipage={onToggleMultipage}
        isMultipageBeingUpdated={isMultipageUpdating}
      />
      <div
        ref={scrollingContainerRef}
        className={`_overflow-auto ${topShadow ? styles.topShadow : ''} spacing-p-l-4 spacing-p-t-2 spacing-p-r-4 spacing-m-0 spacing-m-t-2 spacing-p-b-4 _full-width _full-height`}
      >
        {getRecommendedDesignsGrid()}
        {getDesigns()}
        {getNoResultsUI()}
      </div>
    </div>
  );
}
